/**
 * Marketing sources — web analytics, paid media, organic search and
 * social. Gated to the `marketing` module. The two blended-efficiency
 * metrics (MER, CAC) additionally need order data and therefore the
 * `sales` module; the catalog hides them when either is missing.
 */

import type { DatasetBag, SocialDailyLite } from '../datasets';
import type { BreakdownDef, ForecastSourceDef, MetricDef, TableDef } from '../catalogTypes';
import type { SeriesPoint } from '../types';
import { eachBucket, todayIso } from '../series';
import { activeOrders } from './sales';
import {
  dailySeries,
  dailyWeightedAvg,
  groupSum,
  inWindow,
  monthlySeries,
  sum,
  titleCase,
  toSlices,
} from './helpers';

const MODULE = 'marketing';

function webDaily(bag: DatasetBag) {
  return bag.web?.daily ?? [];
}

function adSpendDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(bag.ads ?? [], (r) => r.day, (r) => r.spend_cents);
}

/**
 * Followers are a level, not a flow: the daily total carries each account's
 * last known count forward so a platform that skipped a day doesn't read
 * as a drop.
 */
function followersDaily(bag: DatasetBag): SeriesPoint[] {
  const rows = bag.social ?? [];
  if (rows.length === 0) return [];
  const byAccount = new Map<string, Map<string, number>>();
  let first = rows[0].day;
  for (const r of rows) {
    if (r.day < first) first = r.day;
    const m = byAccount.get(r.account_id) ?? new Map<string, number>();
    m.set(r.day, r.followers);
    byAccount.set(r.account_id, m);
  }
  const days = eachBucket(first, todayIso(), 'day');
  const last = new Map<string, number>();
  return days.map((day) => {
    for (const [account, m] of byAccount) {
      const v = m.get(day);
      if (v !== undefined) last.set(account, v);
    }
    return { t: day, v: sum(Array.from(last.values())) };
  });
}

function latestFollowersByPlatform(bag: DatasetBag): Map<string, number> {
  const latest = new Map<string, SocialDailyLite>();
  for (const r of bag.social ?? []) {
    const prev = latest.get(r.account_id);
    if (!prev || r.day > prev.day) latest.set(r.account_id, r);
  }
  return groupSum(Array.from(latest.values()), (r) => titleCase(r.platform), (r) => r.followers);
}

function noData(key: 'web' | 'ads' | 'search' | 'social', label: string) {
  return (bag: DatasetBag): string | null => {
    const value = bag[key];
    if (!value) return null;
    const empty = Array.isArray(value) ? value.length === 0 : value.daily.length === 0;
    return empty ? `No ${label} data yet — connect the integration under Settings.` : null;
  };
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export const MARKETING_METRICS: MetricDef[] = [
  {
    id: 'mkt.sessions',
    kind: 'metric',
    label: 'Website sessions',
    group: 'Marketing',
    description: 'Sessions reported by web analytics.',
    module: MODULE,
    requires: ['web'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(webDaily(bag), (r) => r.day, (r) => r.sessions),
    nonNegative: true,
    available: noData('web', 'web analytics'),
  },
  {
    id: 'mkt.active_users',
    kind: 'metric',
    label: 'Active users',
    group: 'Marketing',
    description: 'Daily active users on the website.',
    module: MODULE,
    requires: ['web'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(webDaily(bag), (r) => r.day, (r) => r.active_users),
    nonNegative: true,
  },
  {
    id: 'mkt.new_users',
    kind: 'metric',
    label: 'New users',
    group: 'Marketing',
    description: 'First-time visitors.',
    module: MODULE,
    requires: ['web'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(webDaily(bag), (r) => r.day, (r) => r.new_users),
    nonNegative: true,
  },
  {
    id: 'mkt.engaged_sessions',
    kind: 'metric',
    label: 'Engaged sessions',
    group: 'Marketing',
    description: 'Sessions that lasted 10+ seconds, converted, or viewed 2+ pages.',
    module: MODULE,
    requires: ['web'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(webDaily(bag), (r) => r.day, (r) => r.engaged_sessions),
    nonNegative: true,
  },
  {
    id: 'mkt.engagement_rate',
    kind: 'metric',
    label: 'Engagement rate',
    group: 'Marketing',
    description: 'Engaged sessions as a share of all sessions.',
    module: MODULE,
    requires: ['web'],
    unit: 'percent',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.engaged_sessions', den: 'mkt.sessions' },
  },
  {
    id: 'mkt.web_conversions',
    kind: 'metric',
    label: 'Website conversions',
    group: 'Marketing',
    description: 'Conversion events reported by web analytics.',
    module: MODULE,
    requires: ['web'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(webDaily(bag), (r) => r.day, (r) => r.conversions),
    nonNegative: true,
  },
  {
    id: 'mkt.web_conversion_rate',
    kind: 'metric',
    label: 'Website conversion rate',
    group: 'Marketing',
    description: 'Conversions per session.',
    module: MODULE,
    requires: ['web'],
    unit: 'percent',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.web_conversions', den: 'mkt.sessions' },
  },
  {
    id: 'mkt.engagement_time',
    kind: 'metric',
    label: 'Engagement time per user',
    group: 'Marketing',
    description: 'Average engaged seconds per active user.',
    module: MODULE,
    requires: ['web'],
    unit: 'seconds',
    direction: 'up',
    grain: 'day',
    agg: 'avg',
    series: (bag) =>
      dailyWeightedAvg(
        webDaily(bag),
        (r) => r.day,
        (r) => (r.active_users > 0 ? r.user_engagement_seconds / r.active_users : null),
        (r) => r.active_users,
      ),
  },
  {
    id: 'mkt.ad_spend',
    kind: 'metric',
    label: 'Ad spend',
    group: 'Marketing',
    description: 'Paid media spend across connected ad accounts.',
    module: MODULE,
    requires: ['ads'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'day',
    agg: 'sum',
    series: adSpendDaily,
    nonNegative: true,
    available: noData('ads', 'ad platform'),
  },
  {
    id: 'mkt.ad_impressions',
    kind: 'metric',
    label: 'Ad impressions',
    group: 'Marketing',
    description: 'Times an ad was shown.',
    module: MODULE,
    requires: ['ads'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.ads ?? [], (r) => r.day, (r) => r.impressions),
    nonNegative: true,
  },
  {
    id: 'mkt.ad_clicks',
    kind: 'metric',
    label: 'Ad clicks',
    group: 'Marketing',
    description: 'Clicks on paid ads.',
    module: MODULE,
    requires: ['ads'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.ads ?? [], (r) => r.day, (r) => r.clicks),
    nonNegative: true,
  },
  {
    id: 'mkt.ctr',
    kind: 'metric',
    label: 'Ad click-through rate',
    group: 'Marketing',
    description: 'Clicks per impression.',
    module: MODULE,
    requires: ['ads'],
    unit: 'percent',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.ad_clicks', den: 'mkt.ad_impressions' },
  },
  {
    id: 'mkt.cpc',
    kind: 'metric',
    label: 'Cost per click',
    group: 'Marketing',
    description: 'Ad spend divided by clicks.',
    module: MODULE,
    requires: ['ads'],
    unit: 'currency',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.ad_spend', den: 'mkt.ad_clicks' },
  },
  {
    id: 'mkt.ad_conversions',
    kind: 'metric',
    label: 'Ad conversions',
    group: 'Marketing',
    description: 'Conversions attributed by the ad platforms.',
    module: MODULE,
    requires: ['ads'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.ads ?? [], (r) => r.day, (r) => r.conversions),
    nonNegative: true,
  },
  {
    id: 'mkt.cpa',
    kind: 'metric',
    label: 'Cost per acquisition',
    group: 'Marketing',
    description: 'Ad spend divided by attributed conversions.',
    module: MODULE,
    requires: ['ads'],
    unit: 'currency',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.ad_spend', den: 'mkt.ad_conversions' },
  },
  {
    id: 'mkt.ad_revenue',
    kind: 'metric',
    label: 'Ad-attributed revenue',
    group: 'Marketing',
    description: 'Revenue the ad platforms attribute to campaigns.',
    module: MODULE,
    requires: ['ads'],
    unit: 'currency',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.ads ?? [], (r) => r.day, (r) => r.revenue_cents),
    nonNegative: true,
  },
  {
    id: 'mkt.roas',
    kind: 'metric',
    label: 'ROAS',
    group: 'Marketing',
    description: 'Ad-attributed revenue per dollar of ad spend.',
    module: MODULE,
    requires: ['ads'],
    unit: 'multiple',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.ad_revenue', den: 'mkt.ad_spend' },
  },
  {
    id: 'mkt.mer',
    kind: 'metric',
    label: 'Marketing efficiency ratio',
    group: 'Marketing',
    description: 'Total order revenue per dollar of ad spend (blended, not attributed).',
    module: MODULE,
    requires: ['ads', 'orders'],
    unit: 'multiple',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'sales.revenue', den: 'mkt.ad_spend' },
  },
  {
    id: 'mkt.blended_cac',
    kind: 'metric',
    label: 'Blended CAC',
    group: 'Marketing',
    description: 'Ad spend divided by first-time customers in the window.',
    module: MODULE,
    requires: ['ads', 'orders'],
    unit: 'currency',
    direction: 'down',
    grain: 'window',
    agg: 'sum',
    ratio: { num: 'mkt.ad_spend', den: 'sales.new_customers' },
  },
  {
    id: 'mkt.paid_share_of_orders',
    kind: 'metric',
    label: 'Ad conversions per order',
    group: 'Marketing',
    description: 'Attributed ad conversions as a share of all orders — above 100% means platforms over-claim.',
    module: MODULE,
    requires: ['ads', 'orders'],
    unit: 'percent',
    direction: 'neutral',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.ad_conversions', den: 'sales.orders' },
  },
  {
    id: 'mkt.search_clicks',
    kind: 'metric',
    label: 'Organic search clicks',
    group: 'Marketing',
    description: 'Clicks from Google Search results.',
    module: MODULE,
    requires: ['search'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.search ?? [], (r) => r.day, (r) => r.clicks),
    nonNegative: true,
    available: noData('search', 'Search Console'),
  },
  {
    id: 'mkt.search_impressions',
    kind: 'metric',
    label: 'Search impressions',
    group: 'Marketing',
    description: 'Times the site appeared in Google Search results.',
    module: MODULE,
    requires: ['search'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.search ?? [], (r) => r.day, (r) => r.impressions),
    nonNegative: true,
  },
  {
    id: 'mkt.search_ctr',
    kind: 'metric',
    label: 'Search click-through rate',
    group: 'Marketing',
    description: 'Search clicks per impression.',
    module: MODULE,
    requires: ['search'],
    unit: 'percent',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.search_clicks', den: 'mkt.search_impressions' },
  },
  {
    id: 'mkt.search_position',
    kind: 'metric',
    label: 'Average search position',
    group: 'Marketing',
    description: 'Impression-weighted average ranking position (lower is better).',
    module: MODULE,
    requires: ['search'],
    unit: 'score',
    direction: 'down',
    grain: 'day',
    agg: 'avg',
    series: (bag) =>
      dailyWeightedAvg(bag.search ?? [], (r) => r.day, (r) => (r.position > 0 ? r.position : null), (r) => Math.max(1, r.impressions)),
  },
  {
    id: 'mkt.followers',
    kind: 'metric',
    label: 'Social followers',
    group: 'Marketing',
    description: 'Combined followers across connected social accounts.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'last',
    series: followersDaily,
    nonNegative: true,
    available: noData('social', 'social'),
  },
  {
    id: 'mkt.follower_growth',
    kind: 'metric',
    label: 'Net new followers',
    group: 'Marketing',
    description: 'Follower change across all accounts.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.social ?? [], (r) => r.day, (r) => r.follower_delta),
  },
  {
    id: 'mkt.social_reach',
    kind: 'metric',
    label: 'Social reach',
    group: 'Marketing',
    description: 'Unique accounts reached.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.social ?? [], (r) => r.day, (r) => r.reach),
    nonNegative: true,
  },
  {
    id: 'mkt.social_impressions',
    kind: 'metric',
    label: 'Social impressions',
    group: 'Marketing',
    description: 'Times content was displayed.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.social ?? [], (r) => r.day, (r) => r.impressions),
    nonNegative: true,
  },
  {
    id: 'mkt.social_engagements',
    kind: 'metric',
    label: 'Social engagements',
    group: 'Marketing',
    description: 'Likes, comments, shares and saves.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.social ?? [], (r) => r.day, (r) => r.engagements),
    nonNegative: true,
  },
  {
    id: 'mkt.social_engagement_rate',
    kind: 'metric',
    label: 'Social engagement rate',
    group: 'Marketing',
    description: 'Engagements per impression.',
    module: MODULE,
    requires: ['social'],
    unit: 'percent',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    ratio: { num: 'mkt.social_engagements', den: 'mkt.social_impressions' },
  },
  {
    id: 'mkt.posts_published',
    kind: 'metric',
    label: 'Posts published',
    group: 'Marketing',
    description: 'Posts across all social accounts.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: (bag) => dailySeries(bag.social ?? [], (r) => r.day, (r) => r.posts_published),
    nonNegative: true,
  },
];

// ---------------------------------------------------------------------------
// Breakdowns
// ---------------------------------------------------------------------------

export const MARKETING_BREAKDOWNS: BreakdownDef[] = [
  {
    id: 'mkt.sessions_by_channel',
    kind: 'breakdown',
    label: 'Sessions by channel',
    group: 'Marketing',
    description: 'Where website traffic comes from (organic, paid, direct, …).',
    module: MODULE,
    requires: ['web'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          (bag.web?.referrers ?? []).filter((r) => inWindow(r.day, start, end)),
          (r) => titleCase(r.channel),
          (r) => r.sessions,
        ),
      ),
  },
  {
    id: 'mkt.spend_by_platform',
    kind: 'breakdown',
    label: 'Ad spend by platform',
    group: 'Marketing',
    description: 'Paid media spend per ad platform.',
    module: MODULE,
    requires: ['ads'],
    unit: 'currency',
    slices: (bag, start, end) =>
      toSlices(groupSum((bag.ads ?? []).filter((r) => inWindow(r.day, start, end)), (r) => titleCase(r.platform), (r) => r.spend_cents)),
  },
  {
    id: 'mkt.spend_by_campaign',
    kind: 'breakdown',
    label: 'Ad spend by campaign',
    group: 'Marketing',
    description: 'Paid media spend per campaign.',
    module: MODULE,
    requires: ['ads'],
    unit: 'currency',
    slices: (bag, start, end) =>
      toSlices(groupSum((bag.ads ?? []).filter((r) => inWindow(r.day, start, end)), (r) => r.campaign, (r) => r.spend_cents)),
  },
  {
    id: 'mkt.conversions_by_platform',
    kind: 'breakdown',
    label: 'Ad conversions by platform',
    group: 'Marketing',
    description: 'Attributed conversions per ad platform.',
    module: MODULE,
    requires: ['ads'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(groupSum((bag.ads ?? []).filter((r) => inWindow(r.day, start, end)), (r) => titleCase(r.platform), (r) => r.conversions)),
  },
  {
    id: 'mkt.followers_by_platform',
    kind: 'breakdown',
    label: 'Followers by platform',
    group: 'Marketing',
    description: 'Latest follower count per social platform.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    slices: (bag) => toSlices(latestFollowersByPlatform(bag)),
  },
  {
    id: 'mkt.engagements_by_platform',
    kind: 'breakdown',
    label: 'Engagements by platform',
    group: 'Marketing',
    description: 'Social engagements per platform in the window.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    slices: (bag, start, end) =>
      toSlices(
        groupSum((bag.social ?? []).filter((r) => inWindow(r.day, start, end)), (r) => titleCase(r.platform), (r) => r.engagements),
      ),
  },
];

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export const MARKETING_TABLES: TableDef[] = [
  {
    id: 'mkt.campaigns_table',
    kind: 'table',
    label: 'Campaign performance',
    group: 'Marketing',
    description: 'Spend, clicks, conversions, CPA and ROAS per campaign for the window.',
    module: MODULE,
    requires: ['ads'],
    columns: [
      { key: 'campaign', label: 'Campaign' },
      { key: 'platform', label: 'Platform' },
      { key: 'spend', label: 'Spend', unit: 'currency' },
      { key: 'impressions', label: 'Impr.', unit: 'count' },
      { key: 'clicks', label: 'Clicks', unit: 'count' },
      { key: 'ctr', label: 'CTR', unit: 'percent' },
      { key: 'conversions', label: 'Conv.', unit: 'count' },
      { key: 'cpa', label: 'CPA', unit: 'currency' },
      { key: 'roas', label: 'ROAS', unit: 'multiple' },
    ],
    defaultColumns: ['campaign', 'spend', 'clicks', 'conversions', 'roas'],
    defaultSort: { key: 'spend', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const acc = new Map<string, { platform: string; spend: number; impressions: number; clicks: number; conversions: number; revenue: number }>();
      for (const r of (bag.ads ?? []).filter((x) => inWindow(x.day, start, end))) {
        const key = `${r.platform}:${r.campaign}`;
        const row = acc.get(key) ?? { platform: r.platform, spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 };
        row.spend += r.spend_cents;
        row.impressions += r.impressions;
        row.clicks += r.clicks;
        row.conversions += r.conversions;
        row.revenue += r.revenue_cents;
        acc.set(key, row);
      }
      return Array.from(acc.entries()).map(([key, r]) => ({
        key,
        cells: {
          campaign: key.slice(key.indexOf(':') + 1),
          platform: titleCase(r.platform),
          spend: r.spend,
          impressions: r.impressions,
          clicks: r.clicks,
          ctr: r.impressions ? r.clicks / r.impressions : null,
          conversions: r.conversions,
          cpa: r.conversions ? r.spend / r.conversions : null,
          roas: r.spend ? r.revenue / r.spend : null,
        },
      }));
    },
  },
  {
    id: 'mkt.channels_table',
    kind: 'table',
    label: 'Traffic by channel',
    group: 'Marketing',
    description: 'Sessions and users per acquisition channel, with share of total.',
    module: MODULE,
    requires: ['web'],
    columns: [
      { key: 'channel', label: 'Channel' },
      { key: 'sessions', label: 'Sessions', unit: 'count' },
      { key: 'users', label: 'Users', unit: 'count' },
      { key: 'share', label: 'Share', unit: 'percent' },
    ],
    defaultSort: { key: 'sessions', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const rows = (bag.web?.referrers ?? []).filter((r) => inWindow(r.day, start, end));
      const sessions = groupSum(rows, (r) => titleCase(r.channel), (r) => r.sessions);
      const users = groupSum(rows, (r) => titleCase(r.channel), (r) => r.active_users);
      const total = sum(Array.from(sessions.values()));
      return Array.from(sessions.entries()).map(([channel, s]) => ({
        key: channel,
        cells: { channel, sessions: s, users: users.get(channel) ?? 0, share: total ? s / total : null },
      }));
    },
  },
  {
    id: 'mkt.social_table',
    kind: 'table',
    label: 'Social accounts',
    group: 'Marketing',
    description: 'Followers, growth, reach and engagement per platform for the window.',
    module: MODULE,
    requires: ['social'],
    columns: [
      { key: 'platform', label: 'Platform' },
      { key: 'followers', label: 'Followers', unit: 'count' },
      { key: 'growth', label: 'Net new', unit: 'count', signed: true },
      { key: 'reach', label: 'Reach', unit: 'count' },
      { key: 'engagements', label: 'Engagements', unit: 'count' },
      { key: 'posts', label: 'Posts', unit: 'count' },
    ],
    defaultSort: { key: 'followers', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const followers = latestFollowersByPlatform(bag);
      const rows = (bag.social ?? []).filter((r) => inWindow(r.day, start, end));
      const growth = groupSum(rows, (r) => titleCase(r.platform), (r) => r.follower_delta);
      const reach = groupSum(rows, (r) => titleCase(r.platform), (r) => r.reach);
      const engagements = groupSum(rows, (r) => titleCase(r.platform), (r) => r.engagements);
      const posts = groupSum(rows, (r) => titleCase(r.platform), (r) => r.posts_published);
      const platforms = new Set([...followers.keys(), ...growth.keys()]);
      return Array.from(platforms).map((platform) => ({
        key: platform,
        cells: {
          platform,
          followers: followers.get(platform) ?? 0,
          growth: growth.get(platform) ?? 0,
          reach: reach.get(platform) ?? 0,
          engagements: engagements.get(platform) ?? 0,
          posts: posts.get(platform) ?? 0,
        },
      }));
    },
  },
  {
    id: 'mkt.orders_vs_sessions_table',
    kind: 'table',
    label: 'Traffic to orders by month',
    group: 'Marketing',
    description: 'Sessions, orders and site-wide conversion rate per month.',
    module: MODULE,
    requires: ['web', 'orders'],
    columns: [
      { key: 'month', label: 'Month', date: true },
      { key: 'sessions', label: 'Sessions', unit: 'count' },
      { key: 'orders', label: 'Orders', unit: 'count' },
      { key: 'rate', label: 'Orders / sessions', unit: 'percent' },
    ],
    defaultSort: { key: 'month', dir: 'desc' },
    defaultLimit: 12,
    rows: (bag, start, end) => {
      const sessions = new Map(
        monthlySeries(webDaily(bag).filter((r) => inWindow(r.day, start, end)), (r) => r.day, (r) => r.sessions).map((p) => [p.t, p.v]),
      );
      const orders = new Map(
        monthlySeries(activeOrders(bag).filter((o) => inWindow(o.day, start, end)), (o) => o.day, () => 1).map((p) => [p.t, p.v]),
      );
      const months = Array.from(new Set([...sessions.keys(), ...orders.keys()])).sort();
      return months.map((month) => {
        const s = sessions.get(month) ?? 0;
        const o = orders.get(month) ?? 0;
        return { key: month, cells: { month, sessions: s, orders: o, rate: s ? o / s : null } };
      });
    },
  },
];

// ---------------------------------------------------------------------------
// Forecast sources
// ---------------------------------------------------------------------------

export const MARKETING_FORECASTS: ForecastSourceDef[] = [
  {
    id: 'fc.sessions',
    kind: 'forecast',
    label: 'Website sessions',
    group: 'Marketing',
    description: 'Monthly sessions projected forward.',
    module: MODULE,
    requires: ['web'],
    unit: 'count',
    nonNegative: true,
    history: (bag) => monthlySeries(webDaily(bag), (r) => r.day, (r) => r.sessions),
  },
  {
    id: 'fc.ad_spend',
    kind: 'forecast',
    label: 'Ad spend',
    group: 'Marketing',
    description: 'Monthly paid media spend projected forward.',
    module: MODULE,
    requires: ['ads'],
    unit: 'currency',
    nonNegative: true,
    history: (bag) => monthlySeries(bag.ads ?? [], (r) => r.day, (r) => r.spend_cents),
  },
  {
    id: 'fc.followers',
    kind: 'forecast',
    label: 'Social followers',
    group: 'Marketing',
    description: 'Month-end combined followers projected forward.',
    module: MODULE,
    requires: ['social'],
    unit: 'count',
    nonNegative: true,
    fill: 'carry',
    history: (bag) => {
      const daily = followersDaily(bag);
      const byMonth = new Map<string, number>();
      for (const p of daily) byMonth.set(`${p.t.slice(0, 7)}-01`, p.v);
      return Array.from(byMonth.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([t, v]) => ({ t, v }));
    },
  },
];
